// SettingsContext — loads app-wide settings (hidden feature modules, payslip
// settings) and exposes helpers to read/update them. Backed by the `app_settings` table.
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '../supabaseClient';

const SettingsContext = createContext(null);

const HIDDEN_MODULES_KEY = 'hidden_modules';
const PAYSLIP_SETTINGS_KEY = 'payslip_settings';
const PAYROLL_SETTINGS_KEY = 'payroll_settings';
const PERMISSIONS_KEY = 'permissions';

// Payslip defaults: YTD box OFF, tracking from 1 Jan of the current year.
// value shape for key 'payslip_settings': { "showYtd": false, "ytdStartDate": "2026-01-01" }
const DEFAULT_PAYSLIP_SETTINGS = {
  showYtd: false,
  ytdStartDate: `${new Date().getFullYear()}-01-01`
};

// Payroll defaults: warn when total deductions exceed 50% of actual wages.
// value shape for key 'payroll_settings': { "maxDeductionWarningPct": 50 }
const DEFAULT_PAYROLL_SETTINGS = {
  maxDeductionWarningPct: 50
};

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within a SettingsProvider');
  return ctx;
};

export const SettingsProvider = ({ children }) => {
  const [hiddenModules, setHiddenModules] = useState([]);
  const [payslipSettings, setPayslipSettings] = useState(DEFAULT_PAYSLIP_SETTINGS);
  const [payrollSettings, setPayrollSettings] = useState(DEFAULT_PAYROLL_SETTINGS);
  const [allowAdminDelete, setAllowAdminDelete] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      setLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('key, value')
        .in('key', [HIDDEN_MODULES_KEY, PAYSLIP_SETTINGS_KEY, PAYROLL_SETTINGS_KEY, PERMISSIONS_KEY]);

      if (error) throw error;
      const rows = data || [];
      const hiddenRow = rows.find((r) => r.key === HIDDEN_MODULES_KEY);
      const payslipRow = rows.find((r) => r.key === PAYSLIP_SETTINGS_KEY);
      const payrollRow = rows.find((r) => r.key === PAYROLL_SETTINGS_KEY);
      const permissionsRow = rows.find((r) => r.key === PERMISSIONS_KEY);

      setHiddenModules(Array.isArray(hiddenRow?.value?.modules) ? hiddenRow.value.modules : []);
      setPayslipSettings({
        showYtd: payslipRow?.value?.showYtd === true,
        ytdStartDate: payslipRow?.value?.ytdStartDate || DEFAULT_PAYSLIP_SETTINGS.ytdStartDate
      });
      const pct = Number(payrollRow?.value?.maxDeductionWarningPct);
      setPayrollSettings({
        maxDeductionWarningPct: pct >= 1 && pct <= 100 ? pct : DEFAULT_PAYROLL_SETTINGS.maxDeductionWarningPct
      });
      // Default OFF: only super admins can delete unless explicitly enabled
      setAllowAdminDelete(permissionsRow?.value?.allowAdminDelete === true);
    } catch (err) {
      console.error('[Settings] Error fetching settings:', err);
      setHiddenModules([]);
      setPayslipSettings(DEFAULT_PAYSLIP_SETTINGS);
      setPayrollSettings(DEFAULT_PAYROLL_SETTINGS);
      setAllowAdminDelete(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Live updates across all open sessions
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    const channel = supabase
      .channel('app-settings-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'app_settings' },
        () => fetchSettings()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchSettings]);

  const isModuleVisible = useCallback(
    (moduleKey) => !hiddenModules.includes(moduleKey),
    [hiddenModules]
  );

  // Persist a full new list of hidden module keys (super admin only via RLS)
  const saveHiddenModules = useCallback(async (modules) => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }
    try {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('app_settings')
        .upsert(
          {
            key: HIDDEN_MODULES_KEY,
            value: { modules },
            updated_by: userData?.user?.id || null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'key' }
        );
      if (error) throw error;
      setHiddenModules(modules);
      return { success: true };
    } catch (err) {
      console.error('[Settings] Error saving hidden modules:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Persist payslip settings (super admin only via RLS)
  const savePayslipSettings = useCallback(async ({ showYtd, ytdStartDate }) => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }
    try {
      const next = {
        showYtd: showYtd === true,
        ytdStartDate: ytdStartDate || DEFAULT_PAYSLIP_SETTINGS.ytdStartDate
      };
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('app_settings')
        .upsert(
          {
            key: PAYSLIP_SETTINGS_KEY,
            value: next,
            updated_by: userData?.user?.id || null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'key' }
        );
      if (error) throw error;
      setPayslipSettings(next);
      return { success: true };
    } catch (err) {
      console.error('[Settings] Error saving payslip settings:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Persist payroll settings (super admin only via RLS)
  const savePayrollSettings = useCallback(async ({ maxDeductionWarningPct }) => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }
    try {
      const pct = Number(maxDeductionWarningPct);
      const next = {
        maxDeductionWarningPct: pct >= 1 && pct <= 100 ? pct : DEFAULT_PAYROLL_SETTINGS.maxDeductionWarningPct
      };
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('app_settings')
        .upsert(
          {
            key: PAYROLL_SETTINGS_KEY,
            value: next,
            updated_by: userData?.user?.id || null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'key' }
        );
      if (error) throw error;
      setPayrollSettings(next);
      return { success: true };
    } catch (err) {
      console.error('[Settings] Error saving payroll settings:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Persist the admin-delete guard (super admin only via RLS)
  const saveAllowAdminDelete = useCallback(async (enabled) => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }
    try {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('app_settings')
        .upsert(
          {
            key: PERMISSIONS_KEY,
            value: { allowAdminDelete: enabled === true },
            updated_by: userData?.user?.id || null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'key' }
        );
      if (error) throw error;
      setAllowAdminDelete(enabled === true);
      return { success: true };
    } catch (err) {
      console.error('[Settings] Error saving permissions:', err);
      return { success: false, error: err.message };
    }
  }, []);

  const value = {
    hiddenModules,
    payslipSettings,
    payrollSettings,
    allowAdminDelete,
    loading,
    isModuleVisible,
    saveHiddenModules,
    savePayslipSettings,
    savePayrollSettings,
    saveAllowAdminDelete,
    refreshSettings: fetchSettings,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};
